// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/LocationSelector.js

import React, { useEffect } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { API_URLS } from '../api/api';

const LocationSelector = ({
  enableLocation, setEnableLocation,
  jangwats, setJangwats,
  amphures, setAmphures,
  thamboons, setThamboons,
  selectedJangwat, setSelectedJangwat,
  selectedAmphure, setSelectedAmphure,
  selectedThamboon, setSelectedThamboon,
  translations, memberData, mode
}) => {
  useEffect(() => {
    const fetchJangwats = async () => {
      try {
        const response = await fetch(API_URLS.JANGWAT_LIST);
        if (response.ok) {
          const data = await response.json();
          const provinceOptions = data.map((jw) => ({
            label: jw.jangwatNameTH,
            value: jw.jangwatID,
          }));
          setJangwats(provinceOptions);
        }
      } catch (error) {
        console.error('Error fetching Jangwats:', error);
      }
    };
    fetchJangwats();

    // Fetch amphures and thamboons automatically
    const fetchLocationData = async () => {
      if (mode === 'edit' && memberData?.amphoeID) {
        // In edit mode, fetch amphures using amphureID
        try {
          const amphureResponse = await fetch(API_URLS.AMPHURE_SEARCH('amphureID', memberData.amphoeID));
          if (amphureResponse.ok) {
            const amphureData = await amphureResponse.json();
            const amphureOptions = amphureData.map((am) => ({
              label: am.amphureNameTH,
              value: am.amphureID,
            }));
            setAmphures(amphureOptions);
          }
        } catch (error) {
          console.error('Error fetching amphures:', error);
        }

        // Fetch thamboons using thamboonID in edit mode
        if (memberData?.thamboonID) {
          try {
            const thamboonResponse = await fetch(API_URLS.THAMBOON_SEARCH(memberData.thamboonID));
            if (thamboonResponse.ok) {
              const thamboonData = await thamboonResponse.json();
              const thamboonOptions = thamboonData.map((tb) => ({
                label: tb.thamboonNameTH,
                value: tb.thamboonID,
              }));
              setThamboons(thamboonOptions);
            }
          } catch (error) {
            console.error('Error fetching thamboons:', error);
          }
        }
      } else if (selectedJangwat) {
        // Outside edit mode or when jangwatID is selected, fetch amphures using jangwatID
        try {
          const amphureResponse = await fetch(API_URLS.AMPHURE_SEARCH('jangwatID', selectedJangwat));
          if (amphureResponse.ok) {
            const amphureData = await amphureResponse.json();
            const amphureOptions = amphureData.map((am) => ({
              label: am.amphureNameTH,
              value: am.amphureID,
            }));
            setAmphures(amphureOptions);
            setThamboons([]); // Reset thamboons when changing jangwat or amphure
          }
        } catch (error) {
          console.error('Error fetching amphures:', error);
        }

        // Fetch thamboons using jangwatID and amphureID outside edit mode
        if (selectedAmphure) {
          try {
            const thamboonResponse = await fetch(API_URLS.JANGWAT_SEARCH(selectedJangwat, selectedAmphure));
            if (thamboonResponse.ok) {
              const thamboonData = await thamboonResponse.json();
              const thamboonOptions = thamboonData.map((tb) => ({
                label: tb.thamboonNameTH,
                value: tb.thamboonID,
              }));
              setThamboons(thamboonOptions);
            }
          } catch (error) {
            console.error('Error fetching thamboons:', error);
          }
        }
      }
    };

    fetchLocationData();

    // Set initial values
    if (mode === 'edit') {
      if (memberData?.jangwatID) {
        console.log('Setting Jangwat:', { jangwatID: memberData.jangwatID });
        setSelectedJangwat(memberData.jangwatID);
        setEnableLocation(true);
      }
      if (memberData?.amphoeID) {
        console.log('Setting Amphure:', { amphoeID: memberData.amphoeID });
        setSelectedAmphure(memberData.amphoeID);
      }
      if (memberData?.thamboonID) {
        console.log('Setting Thamboon:', { thamboonID: memberData.thamboonID });
        setSelectedThamboon(memberData.thamboonID);
      }
    }
  }, [memberData, mode, selectedJangwat, selectedAmphure, setJangwats, setEnableLocation, setSelectedJangwat, setSelectedAmphure, setSelectedThamboon]);

  const handleJangwatChange = async (e) => {
    setSelectedJangwat(e.value);
    setSelectedAmphure(null);
    setSelectedThamboon(null);
    try {
      const response = await fetch(API_URLS.AMPHURE_SEARCH('jangwatID', e.value));
      if (response.ok) {
        const data = await response.json();
        const amphureOptions = data.map((am) => ({
          label: am.amphureNameTH,
          value: am.amphureID,
        }));
        setAmphures(amphureOptions);
        setThamboons([]);
      }
    } catch (error) {
      console.error('Error fetching amphures:', error);
    }
  };

  const handleAmphureChange = async (e) => {
    setSelectedAmphure(e.value);
    setSelectedThamboon(null);
    try {
      const response = await fetch(API_URLS.JANGWAT_SEARCH(selectedJangwat, e.value));
      if (response.ok) {
        const data = await response.json();
        const thamboonOptions = data.map((tb) => ({
          label: tb.thamboonNameTH,
          value: tb.thamboonID,
        }));
        setThamboons(thamboonOptions);
      }
    } catch (error) {
      console.error('Error fetching thamboons:', error);
    }
  };

  const handleThamboonChange = (e) => {
    setSelectedThamboon(e.value);
  };

  return (
    <>
      <div className="mb-3">
        <label htmlFor="locationSwitch" className="form-label">
          {translations.locationSwitchLabel || "Enable Province / District / Subdistrict?"}
        </label>
        <br />
        <InputSwitch
          id="locationSwitch"
          checked={enableLocation}
          onChange={(e) => setEnableLocation(e.value)}
        />
      </div>
      {enableLocation && (
        <>
          <div className="mb-3">
            <label htmlFor="jangwat" className="form-label">{translations.provinceLabel || "Province"}</label>
            <Dropdown
              value={selectedJangwat}
              options={jangwats}
              onChange={handleJangwatChange}
              optionLabel="label"
              placeholder={translations.provincePlaceholder || "Select a Province"}
              filter
              showClear
              filterBy="label"
              className="w-100"
            />
          </div>

          <div className="mb-3">
            <label htmlFor="amphure" className="form-label">{translations.districtLabel || "District"}</label>
            <Dropdown
              value={selectedAmphure}
              options={amphures}
              onChange={handleAmphureChange}
              optionLabel="label"
              placeholder={translations.districtPlaceholder || "Select a District"}
              filter
              showClear
              filterBy="label"
              className="w-100"
            />
          </div>

          <div className="mb-3">
            <label htmlFor="thamboon" className="form-label">{translations.subdistrictLabel || "Subdistrict"}</label>
            <Dropdown
              value={selectedThamboon}
              options={thamboons}
              onChange={handleThamboonChange}
              optionLabel="label"
              placeholder={translations.subdistrictPlaceholder || "Select a Subdistrict"}
              filter
              showClear
              filterBy="label"
              className="w-100"
            />
          </div>
          
        </>
      )}
    </>
  );
};

export default LocationSelector;